import { useCallback } from 'react';

import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';
import { mutationErrorTone } from '@/lib/api/mutation-toast';

import { AUTH_ERROR_CODES } from './constants';
import { authErrorMessage } from './error-copy';

/**
 * Returns a `notify(error)` that surfaces an auth failure as a toast, in the same tone the rest of
 * the app uses for a failed request: red for a `400` (the request's own contents were rejected),
 * amber warning for everything else — an unregistered phone (404), a wrong password (401), a wrong
 * code, a 5xx. See `mutationErrorTone`.
 *
 * Account-lockout is intentionally skipped — it's shown by the sticky toast in `useLockoutNotice`,
 * so we don't double-notify. Centralizes the catch→toast block that every auth screen would
 * otherwise repeat; the auth mutations carry `meta.suppressErrorToast` so `<MutationToaster>`
 * leaves the failure half to this hook.
 */
export function useAuthErrorToast() {
  const toast = useToast();
  return useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.code === AUTH_ERROR_CODES.ACCOUNT_TEMPORARILY_LOCKED) {
        return;
      }
      const title = authErrorMessage(error);
      if (mutationErrorTone(error) === 'error') {
        toast.error({ title });
        return;
      }
      toast.warning({ title });
    },
    [toast],
  );
}
